import { useState } from "react";

/**
 * Registration.
 *
 * The fields the tournament regulations require: nickname, first and last
 * name, FMJD ID if the player has one, email, mobile number.
 *
 * The FMJD ID is deliberately optional — recruiting players who do not yet
 * have one is a stated goal of the project, so it can never gate registration.
 */

interface Issue {
  field: string;
  message: string;
}

export function Register({ onRegistered }: { onRegistered?: () => void }) {
  const [form, setForm] = useState({
    nickname: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fmjdId: "",
    country: "",
    password: "",
  });
  const [issues, setIssues] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ publicId: string } | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const errorFor = (field: string) => issues.find((i) => i.field === field)?.message;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setIssues([]);

    // Empty optional fields must be omitted, not sent as "", or the server's
    // format validation rejects them.
    const body: Record<string, string> = {
      nickname: form.nickname,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
    };
    for (const key of ["phone", "fmjdId", "country"] as const) {
      if (form[key].trim()) body[key] = form[key].trim();
    }

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        setIssues(data.issues ?? [{ field: "", message: data.error ?? "Registration failed" }]);
        return;
      }
      setDone({ publicId: data.publicId });
      // Registration signs the player in, so the header must update.
      onRegistered?.();
    } catch {
      setIssues([{ field: "", message: "Could not reach the server" }]);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ maxWidth: 520 }}>
        <h2>Check your email</h2>
        <p>
          Your account has been created. We have sent a confirmation link to{" "}
          <strong>{form.email}</strong>. Open it to verify your address.
        </p>
        <p style={{ fontSize: 14, color: "#666" }}>
          Your player ID is <code>{done.publicId}</code>.
        </p>
        <p style={{ fontSize: 14, color: "#666" }}>
          You are already signed in — confirming your address unlocks tournament
          entry. The link is valid for 15 minutes.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <h2 style={{ marginTop: 0 }}>Create an account</h2>
      <p style={{ color: "#666", fontSize: 14, marginTop: -8 }}>
        One account for every FMJD tournament. Register once, then enter any
        tournament with a single click.
      </p>

      <form onSubmit={submit}>
        <Field label="Nickname" hint="Shown on the board and in the standings">
          <input
            value={form.nickname}
            onChange={set("nickname")}
            style={input(errorFor("nickname"))}
            autoComplete="username"
          />
          <Err msg={errorFor("nickname")} />
        </Field>

        <div style={{ display: "flex", gap: 12 }}>
          <Field label="First name" style={{ flex: 1 }}>
            <input
              value={form.firstName}
              onChange={set("firstName")}
              style={input(errorFor("firstName"))}
            />
            <Err msg={errorFor("firstName")} />
          </Field>
          <Field label="Last name" style={{ flex: 1 }}>
            <input
              value={form.lastName}
              onChange={set("lastName")}
              style={input(errorFor("lastName"))}
            />
            <Err msg={errorFor("lastName")} />
          </Field>
        </div>

        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            style={input(errorFor("email"))}
            autoComplete="email"
          />
          <Err msg={errorFor("email")} />
        </Field>

        <Field label="Mobile telephone" hint="International format, e.g. +31612345678">
          <input
            value={form.phone}
            onChange={set("phone")}
            style={input(errorFor("phone"))}
            placeholder="+31612345678"
            autoComplete="tel"
          />
          <Err msg={errorFor("phone")} />
        </Field>

        <div style={{ display: "flex", gap: 12 }}>
          <Field
            label="FMJD ID"
            hint="Optional — leave blank if you do not have one"
            style={{ flex: 1 }}
          >
            <input value={form.fmjdId} onChange={set("fmjdId")} style={input(errorFor("fmjdId"))} />
            <Err msg={errorFor("fmjdId")} />
          </Field>
          <Field label="Country" hint="Three letters, e.g. USA" style={{ width: 120 }}>
            <input
              value={form.country}
              onChange={set("country")}
              style={input(errorFor("country"))}
              placeholder="USA"
              maxLength={3}
            />
            <Err msg={errorFor("country")} />
          </Field>
        </div>

        <Field label="Password" hint="At least 8 characters">
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            style={input(errorFor("password"))}
            autoComplete="new-password"
          />
          <Err msg={errorFor("password")} />
        </Field>

        {issues.some((i) => !i.field) && (
          <div style={{ color: "#c62828", fontSize: 14, marginBottom: 12 }}>
            {issues.find((i) => !i.field)?.message}
          </div>
        )}

        <button type="submit" disabled={busy} style={submitBtn(busy)}>
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
  style,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Err({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <div style={{ fontSize: 12, color: "#c62828", marginTop: 3 }}>{msg}</div>;
}

const input = (error?: string): React.CSSProperties => ({
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: `1px solid ${error ? "#c62828" : "#ccc"}`,
  borderRadius: 6,
  boxSizing: "border-box",
});

const submitBtn = (busy: boolean): React.CSSProperties => ({
  padding: "10px 20px",
  fontSize: 15,
  fontWeight: 600,
  color: "#fff",
  background: busy ? "#90a4ae" : "#00695c",
  border: "none",
  borderRadius: 6,
  cursor: busy ? "default" : "pointer",
});
